import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, message, Space, Spin, Table } from 'antd';
import { SaveOutlined, EditOutlined, UploadOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getImagePath } from '../../../utils/format/valueFormat';
import { useObjectsApi } from '../../../api/objects/objects';
import { TScene } from '../../../models/objects/objects';
import { CODIFICATION_ROLES } from '../../../constant/codification';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';

const SceneList: React.FC = () => {
    const { t } = useTranslation();
    const { isRoleExist } = useTilmiAuth();
    const { getScenes, updateScene, createScene, deleteScene, uploadSceneImage } = useObjectsApi();
    const [scenes, setScenes] = useState<TScene[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [addingId, setAddingId] = useState<number>(0);
    const [loadingRow, setLoadingRow] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);


    useEffect(() => {
        setIsLoading(true);
        getScenes()
            .then((resp) => {
                setScenes((resp.data ?? []).filter(e => e.img !== ""));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line
    }, []);

    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingScene, setEditingScene] = useState<TScene | null>(null);

    const edit = (scene: TScene) => {
        setEditingKey(scene.id);
        setEditingScene({ ...scene });
    };

    const handleAddScene = () => {
        const idRow = addingId - 1;
        const newScene: TScene = {
            id: idRow,
            name: "",
            img: 'https://via.placeholder.com/50',
        };

        setAddingId(idRow);
        setEditingKey(idRow);
        setEditingScene(newScene);
        setScenes((prev) => [newScene, ...prev]);
    };

    const save = (id: number) => {
        if (editingScene) {
            setLoadingRow(id);
            (editingScene.id > 0 ? updateScene(editingScene.id, editingScene) : createScene(editingScene))
                .then((resp) => {
                    const sceneResult: TScene = resp.data;
                    const updatedScenes = scenes.map((scene) =>
                        scene.id === id ? sceneResult : scene
                    );
                    setScenes(updatedScenes);
                    message.success(t('message.savedSuccessfully'));
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                    console.error(err);
                })
                .finally(() => {
                    setIsLoading(false);
                    setLoadingRow(null);
                });
        }

        setEditingKey(null);
    };
    const cancel = () => {
        const idRow = editingScene?.id || 0
        if (idRow <= 0) {
            setScenes((prev) => [...prev ?? []].filter(e => e.id !== idRow));
        }
        setEditingKey(null);
        setEditingScene(null);
    }

    // Delete functionality
    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;
        setDeleteLoading(true);
        deleteScene(deleteId)
            .then(() => {
                setScenes(scenes.filter((scene) => scene.id !== deleteId));
                message.success(t('message.deletedSuccessfully'));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };


    const uploadImage = (scene: TScene) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.onchange = async (event: any) => {
            const file = event.target.files[0];
            if (file) {
                setLoadingRow(scene.id);
                const formData = new FormData();
                formData.append('image', file);

                uploadSceneImage(scene.id, formData)
                    .then((resp) => {
                        const sceneResult: TScene = resp.data;
                        const updatedScenes = scenes.map((crs) =>
                            crs.id === scene.id ? sceneResult : crs
                        );
                        setScenes(updatedScenes);
                        message.success(t('message.savedSuccessfully'));
                    })
                    .catch((err) => {
                        message.error(err.response.data ?? err.message);
                        console.error(err);
                    })
                    .finally(() => {
                        setLoadingRow(null);
                    });
            }
        };

        input.click();
    };

    const columns: ColumnsType<TScene> = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            render: (text: string, record: TScene) =>
                record.img ? (
                    <img
                        src={getImagePath(record.img, true)}
                        alt="scene-thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                ) : (
                    'N/A'
                ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: TScene) =>
                editingKey === record.id ? (
                    <Input
                        value={editingScene?.name}
                        onChange={(e) => {
                            if (editingScene) {
                                setEditingScene({ ...editingScene, name: e.target.value });
                            }
                        }}
                    />
                ) : (
                    text
                ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },

        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right' as 'right',
            width: 180,
            render: (_: any, record: TScene) => (
                <Space size="middle">
                    {loadingRow === record.id ? ( // Affiche le spinner si la ligne est en chargement
                        <Spin />
                    ) : (
                        editingKey === record.id ? (
                            <>
                                <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                                <Button icon={<StopOutlined />} onClick={cancel} />
                            </>
                        ) : (
                            <>
                                <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                                <Button icon={<UploadOutlined />} onClick={() => uploadImage(record)} />
                                {
                                    isRoleExist(CODIFICATION_ROLES.DELETE_COURSE) && (
                                        <Button
                                            icon={<DeleteOutlined />}
                                            danger
                                            onClick={() => confirmDelete(record.id)}
                                            loading={deleteId === record.id && deleteLoading}
                                        />
                                    )
                                }
                            </>
                        )
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleAddScene}>
                    {t('button.add')}
                </Button>
            </Space>
            <Table<TScene>
                columns={columns}
                dataSource={scenes}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 1000 }}
            />
            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title="Confirm Deletion"
                description="Are you sure you want to delete this subject? This action is irreversible."
            />
        </>
    );
};

export default SceneList;
